import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, API } from "../App";
import { LogoFull } from "./Logo";
import axios from "axios";
import { Check, Star, Zap, FileText, Users, Clock, ArrowRight, Loader2 } from "lucide-react";

const LifetimeDeal = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [ltdStatus, setLtdStatus] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    axios.get(`${API}/ltd/status`).then(({ data }) => setLtdStatus(data)).catch(console.error);
  }, []);

  const handleGetDeal = async () => {
    if (!user) {
      navigate("/register");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/ltd/checkout`, {
        origin_url: window.location.origin
      });
      if (data.url) window.location.href = data.url;
    } catch (err) {
      const msg = err.response?.data?.detail || "Something went wrong";
      if (msg.includes("Not authenticated")) {
        navigate("/login");
      } else {
        alert(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  const spotsPercent = ltdStatus ? (ltdStatus.claimed / ltdStatus.total_spots) * 100 : 0;

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      {/* Nav */}
      <nav className="border-b border-white/10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
          <Link to="/" data-testid="ltd-logo">
            <LogoFull size={24} inverted />
          </Link>
          <div className="flex items-center gap-3">
            {user ? (
              <Link to="/dashboard" className="text-[13px] text-white/50 hover:text-white transition-colors" data-testid="ltd-dashboard-link">Dashboard</Link>
            ) : (
              <Link to="/login" className="text-[13px] text-white/50 hover:text-white transition-colors" data-testid="ltd-login-link">Sign in</Link>
            )}
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 pt-12 sm:pt-20 pb-12 sm:pb-16">
        <div className="max-w-2xl">
          {ltdStatus?.is_available && (
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#F59E0B]/10 border border-[#F59E0B]/30 mb-6" data-testid="ltd-spots-badge">
              <Star className="w-3.5 h-3.5 text-[#F59E0B]" />
              <span className="text-[12px] font-bold text-[#F59E0B] tracking-wide">
                {ltdStatus.remaining} of {ltdStatus.total_spots} spots left
              </span>
            </div>
          )}

          <h1 className="font-display text-[36px] sm:text-[52px] lg:text-[60px] tracking-[-0.04em] text-white leading-[1.05] mb-5">
            Lifetime access.<br />
            <span className="text-[#F59E0B]">$99. Once.</span>
          </h1>
          <p className="text-[16px] sm:text-[19px] text-white/50 leading-relaxed mb-8 max-w-lg">
            VeroBill is the fastest way to create and send professional invoices. Get lifetime access to every Pro feature — no monthly fees, no limits, ever.
          </p>

          {/* CTA */}
          {ltdStatus?.is_available ? (
            <div className="mb-8">
              <button
                onClick={handleGetDeal}
                disabled={loading}
                className="group px-8 py-4 bg-[#F59E0B] text-[#0A0A0A] font-bold text-[16px] hover:bg-[#D97706] transition-all flex items-center gap-3 disabled:opacity-50"
                data-testid="ltd-hero-cta"
              >
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Star className="w-5 h-5" />}
                {loading ? "Redirecting to checkout..." : "Get lifetime access — $99"}
                {!loading && <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />}
              </button>
              <p className="text-[13px] text-white/30 mt-3">One-time payment. No subscription. No tricks.</p>
            </div>
          ) : (
            <div className="mb-8 px-5 py-4 bg-white/5 border border-white/10">
              <p className="text-[15px] text-white/60">All lifetime spots have been claimed. You can still subscribe to Pro.</p>
              <Link to="/register" className="text-[14px] text-[#1A3DFF] font-semibold hover:underline mt-2 inline-block">Sign up for Pro</Link>
            </div>
          )}

          {/* Progress bar */}
          {ltdStatus?.is_available && (
            <div className="max-w-sm">
              <div className="flex items-center justify-between text-[12px] text-white/30 mb-1.5">
                <span>{ltdStatus.claimed} claimed</span>
                <span>{ltdStatus.total_spots} total</span>
              </div>
              <div className="w-full bg-white/10 h-2">
                <div className="h-2 bg-[#F59E0B] transition-all" style={{ width: `${spotsPercent}%` }} />
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Value Comparison */}
      <section className="border-t border-white/10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
          <h2 className="font-display text-[22px] sm:text-[28px] tracking-[-0.02em] text-white mb-8">
            Why this deal is a no-brainer
          </h2>
          <div className="grid sm:grid-cols-3 gap-4">
            <div className="bg-white/5 border border-white/10 p-5 sm:p-6" data-testid="compare-monthly">
              <p className="text-[12px] text-white/40 uppercase tracking-wide font-semibold mb-2">Monthly Plan</p>
              <p className="font-display text-[28px] text-white/40 line-through">$9<span className="text-[14px]">/mo</span></p>
              <p className="text-[13px] text-white/30 mt-1">= $108/year, recurring</p>
            </div>
            <div className="bg-white/5 border border-white/10 p-5 sm:p-6" data-testid="compare-annual">
              <p className="text-[12px] text-white/40 uppercase tracking-wide font-semibold mb-2">Annual Plan</p>
              <p className="font-display text-[28px] text-white/40 line-through">$79<span className="text-[14px]">/yr</span></p>
              <p className="text-[13px] text-white/30 mt-1">Renews every year</p>
            </div>
            <div className="bg-[#F59E0B]/5 border-2 border-[#F59E0B]/40 p-5 sm:p-6 relative" data-testid="compare-lifetime">
              <div className="absolute -top-3 right-4 px-2 py-0.5 bg-[#F59E0B] text-[10px] font-bold text-[#0A0A0A] uppercase tracking-wide">Best Value</div>
              <p className="text-[12px] text-[#F59E0B] uppercase tracking-wide font-semibold mb-2">Lifetime Deal</p>
              <p className="font-display text-[28px] text-[#F59E0B] font-bold">$99<span className="text-[14px]"> once</span></p>
              <p className="text-[13px] text-[#F59E0B]/60 mt-1">Pay once, use forever</p>
            </div>
          </div>
        </div>
      </section>

      {/* No BS Statement */}
      <section className="border-t border-white/10 bg-white/[0.02]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-12 sm:py-16 text-center">
          <p className="font-display text-[32px] sm:text-[48px] lg:text-[56px] tracking-[-0.04em] text-white leading-[1.1]" data-testid="no-bs-motto">
            No bloat. No BS.<br />
            <span className="text-white/30">Just invoices, done fast.</span>
          </p>
        </div>
      </section>

      {/* What's Included */}
      <section className="border-t border-white/10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
          <h2 className="font-display text-[22px] sm:text-[28px] tracking-[-0.02em] text-white mb-8">
            Everything in Pro. Forever.
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-4">
            {[
              { text: "Unlimited invoices", desc: "No monthly cap, create as many as you need" },
              { text: "Recurring invoices", desc: "Auto-send weekly, monthly, quarterly, or yearly" },
              { text: "3 PDF templates", desc: "Classic, Modern, and Minimal — pick per invoice" },
              { text: "Email from your domain", desc: "Send invoices from your own business email" },
              { text: "Remove VeroBill footer", desc: "Clean, fully white-labelled PDFs" },
              { text: "Unlimited clients & estimates", desc: "Manage contacts and send quotes" },
              { text: "Unlimited expense tracking", desc: "Log expenses, categories, vendors, receipts" },
              { text: "4 payment methods", desc: "Stripe, PayPal, Razorpay UPI, and Wise" },
              { text: "12 country tax templates", desc: "Auto-fill VAT, GST, Sales Tax + compliance" },
              { text: "P&L analytics dashboard", desc: "Revenue, expenses, profit margin at a glance" },
              { text: "All future updates", desc: "New features and improvements, free forever" },
              { text: "Priority support", desc: "Direct access to the founder" }
            ].map((feature, i) => (
              <div key={i} className="flex gap-3 py-2" data-testid={`ltd-feature-${i}`}>
                <Check className="w-4 h-4 text-[#F59E0B] flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-[14px] text-white font-medium">{feature.text}</p>
                  <p className="text-[13px] text-white/40">{feature.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Speed Section */}
      <section className="border-t border-white/10 bg-white/[0.02]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
          <h2 className="font-display text-[22px] sm:text-[28px] tracking-[-0.02em] text-white mb-3">
            Built for speed, not bloat
          </h2>
          <p className="text-[15px] text-white/40 mb-8 max-w-lg">
            Most invoice tools make you click through 10 screens. VeroBill does it in 3 steps, under 30 seconds.
          </p>
          <div className="grid sm:grid-cols-3 gap-6">
            {[
              { step: "01", icon: Users, title: "Pick your client", desc: "Select from saved clients or add a new one inline" },
              { step: "02", icon: FileText, title: "Add line items", desc: "Description, qty, rate — that's it" },
              { step: "03", icon: Zap, title: "Send instantly", desc: "Email, share link, or download PDF" }
            ].map((s, i) => (
              <div key={i} className="flex gap-4">
                <span className="font-display text-[32px] text-white/10 leading-none">{s.step}</span>
                <div>
                  <s.icon className="w-5 h-5 text-[#1A3DFF] mb-2" />
                  <p className="font-semibold text-[15px] text-white mb-1">{s.title}</p>
                  <p className="text-[13px] text-white/40">{s.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      {ltdStatus?.is_available && (
        <section className="border-t border-white/10">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 py-12 sm:py-16 text-center">
            <h2 className="font-display text-[28px] sm:text-[36px] tracking-[-0.03em] text-white mb-3">
              Don't miss out
            </h2>
            <p className="text-[15px] text-white/40 mb-8">
              Only <span className="text-[#F59E0B] font-bold">{ltdStatus.remaining} spots</span> left at this price. Once they're gone, it's monthly or annual only.
            </p>
            <button
              onClick={handleGetDeal}
              disabled={loading}
              className="px-8 py-4 bg-[#F59E0B] text-[#0A0A0A] font-bold text-[16px] hover:bg-[#D97706] transition-all inline-flex items-center gap-3 disabled:opacity-50"
              data-testid="ltd-bottom-cta"
            >
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Star className="w-5 h-5" />}
              {loading ? "Redirecting..." : "Get lifetime access — $99"}
            </button>
          </div>
        </section>
      )}

      {/* Footer */}
      <footer className="border-t border-white/10 py-6">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 flex flex-col sm:flex-row items-center justify-between gap-3">
          <LogoFull size={22} inverted />
          <div className="flex items-center gap-4 text-[13px] text-white/30">
            <Link to="/changelog" className="hover:text-white/60 transition-colors">Changelog</Link>
            <span>&copy; 2026 VeroBill</span>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LifetimeDeal;
